import { List as ImList } from 'immutable';

import type { Address } from '../addresses/addressTypes';
import * as accountsTypes from './accountsTypes';

export function transformApiDataToAccountMap<A extends accountsTypes.BaseAccountProps>(
  data: A & { isNotSynced?: boolean },
): Omit<accountsTypes.BaseAccountProps, 'type'> & { subType: A['subType'] } {
  return {
    ...data,

    id: data.id,
    clientId: data.clientId,
    institutionLoginId: data.institutionLoginId,

    name: data.name ? data.name : 'UNKNOWN',
    subType: data.subType ? data.subType : 'UNKNOWN',
    usageType: data.usageType,
    businessId: data.businessId,

    description: data.description,
    notes: data.notes,

    isExcludedFromAccountBar: Boolean(data.isExcludedFromAccountBar),
    isExcludedFromBudgets: Boolean(data.isExcludedFromBudgets),
    isExcludedFromReports: Boolean(data.isExcludedFromReports),
    isA2ABannerDismissed: Boolean(data.isA2ABannerDismissed),

    onlineBalance: data.onlineBalance,
    onlineBalanceAt: data.onlineBalanceAt,

    fundingAccountId: data.fundingAccountId,

    currency: data.currency,

    isClosed: data.isClosed,
    closedOn: data.closedOn,

    balanceAsOf: data.balanceAsOf,
    balanceAsOfOn: data.balanceAsOfOn,

    currentBalanceAsOf: data.currentBalanceAsOf,
    currentBalanceAsOfOn: data.currentBalanceAsOfOn,

    isConnected: data.isConnected,
    isDeleted: data.isDeleted || data.isNotSynced,
    isIgnored: data.isIgnored,

    modifiedAt: data.modifiedAt,
    createdAt: data.createdAt,

    isBankPendingTxnsExcluded: Boolean(data.isBankPendingTxnsExcluded),
    accountNumberMasked: data.accountNumberMasked,
  };
}

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Accounts to Account API Request Data
// /////////////////////////////////////////////////////////////////////////////

function transformAccountsPayableToRequestData(
  account: accountsTypes.AccountsPayableProps,
): accountsTypes.AccountsPayableProps {
  return {
    type: 'ACCOUNTS_PAYABLE',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformAccountsReceivableToRequestData(
  account: accountsTypes.AccountsReceivableProps,
): accountsTypes.AccountsReceivableProps {
  return {
    type: 'ACCOUNTS_RECEIVABLE',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformBankAccountToRequestData(account: accountsTypes.BankAccountProps): accountsTypes.BankAccountProps {
  return {
    type: 'BANK',
    ...transformBaseAccountToRequestData(account),
    interestRate: account.interestRate,
    maturityDateOn: account.maturityDateOn,
    minimumNoFeeBalance: account.minimumNoFeeBalance,
    monthlyFee: account.monthlyFee,
    freeBillPay: account.freeBillPay,
    atmFeeReimbursement: account.atmFeeReimbursement,
  };
}

function transformCashAccountToRequestData(account: accountsTypes.CashAccountProps): accountsTypes.CashAccountProps {
  return {
    type: 'CASH',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformCreditAccountToRequestData(
  account: accountsTypes.CreditAccountProps,
): accountsTypes.CreditAccountProps {
  return {
    ...(transformBaseAccountToRequestData(account) as accountsTypes.CreditAccountProps),
    type: 'CREDIT',
    interestRate: account.interestRate,
    creditLimit: account.creditLimit,
  };
}

function transformInvestmentAccountToRequestData(
  account: accountsTypes.InvestmentAccountProps,
): accountsTypes.InvestmentAccountProps {
  return {
    type: 'INVESTMENT',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformLoanAccountToRequestData(account: accountsTypes.LoanAccountProps): accountsTypes.LoanAccountProps {
  return {
    type: 'LOAN',
    ...(transformBaseAccountToRequestData(account) as Partial<typeof account>),
  };
}

function transformOtherAssetAccountToRequestData(
  account: accountsTypes.OtherAssetAccountProps,
): accountsTypes.OtherAssetAccountProps {
  return {
    type: 'OTHER_ASSET',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformOtherLiabilityAccountToRequestData(
  account: accountsTypes.OtherLiabilityAccountProps,
): accountsTypes.OtherLiabilityAccountProps {
  return {
    type: 'OTHER_LIABILITY',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformPaymentAccountToRequestData(
  account: accountsTypes.PaymentAccountProps,
): accountsTypes.PaymentAccountProps {
  return {
    type: 'PAYMENT',
    ...transformBaseAccountToRequestData(account),
  };
}

function transformRealEstateAccountToRequestData(
  account: accountsTypes.RealEstateAccountProps,
): accountsTypes.RealEstateAccountProps & { address: Address | null } {
  let address: Address | null = null;
  if (account.line1 || account.line2 || account.city || account.state || account.zipCode) {
    address = {
      line1: account.line1,
      line2: account.line2,
      city: account.city,
      state: account.state,
      zipCode: account.zipCode,
    };
  }
  return {
    type: 'REAL_ESTATE',
    ...(transformBaseAccountToRequestData(account) as Partial<typeof account>),
    address,
  };
}

function transformVehicleAccountToRequestData(
  account: accountsTypes.VehicleAccountProps,
): accountsTypes.VehicleAccountProps {
  return {
    type: 'VEHICLE',
    ...transformBaseAccountToRequestData(account),
    year: account.year,
    makeId: account.makeId,
    make: account.make,
    modelId: account.modelId,
    model: account.model,
    trimId: account.trimId,
    trim: account.trim,
    condition: account.condition,
    mileage: account.mileage,
    zipCode: account.zipCode,
    autoUpdateMiles: account.autoUpdateMiles,
    useCalculatedMilesPerMonth: account.useCalculatedMilesPerMonth,
    calculatedMilesPerMonth: undefined, // read from QCS
    customMilesPerMonth: account.customMilesPerMonth,
  };
}

function transformUnknownAccountToRequestData<A extends accountsTypes.BaseAccountProps>(
  account: A,
): accountsTypes.BaseAccountProps & { type: A['type']; subType: A['subType'] } {
  return {
    type: account.type,
    ...transformBaseAccountToRequestData(account),
  };
}

function transformBusinessAccountToRequestData<A extends accountsTypes.BusinessAccountProps>(
  account: A,
): accountsTypes.BusinessAccountProps & { type: A['type']; subType: A['subType'] } {
  return {
    type: account.type,
    ...transformBaseAccountToRequestData(account),
  };
}

type RemoteCpData = {
  cpData?: { aggregators: Array<accountsTypes.AccountAggregatorProps> };
};

function transformBaseAccountToRequestData<A extends accountsTypes.BaseAccountProps>(
  account: A,
): Omit<accountsTypes.BaseAccountProps, 'type' | 'subType'> & RemoteCpData & { subType: A['subType'] } {
  const id = account.id !== '0' ? account.id : null;

  const requestData: Omit<accountsTypes.BaseAccountProps, 'type'> & RemoteCpData & { subType: A['subType'] } = {
    id,
    clientId: account.clientId,
    isDeleted: account.isDeleted,
    institutionLoginId: account.institutionLoginId,

    name: account.name,
    subType: account.subType,
    usageType: account.usageType,
    businessId: account.businessId,

    description: account.description,
    notes: account.notes,

    isExcludedFromAccountBar: account.isExcludedFromAccountBar,
    isExcludedFromBudgets: account.isExcludedFromBudgets,
    isExcludedFromReports: account.isExcludedFromReports,
    isA2ABannerDismissed: account.isA2ABannerDismissed,
    fundingAccountId: account.fundingAccountId,

    onlineBalance: account.onlineBalance,
    onlineBalanceAt: account.onlineBalanceAt,
    normalizedBalance: account.normalizedBalance,

    currency: account.currency,

    isClosed: account.isClosed,
    closedOn: account.closedOn,

    isIgnored: account.isIgnored,

    balanceAsOf: account.balanceAsOf,
    balanceAsOfOn: account.balanceAsOfOn,

    currentBalanceAsOf: account.currentBalanceAsOf,
    currentBalanceAsOfOn: account.currentBalanceAsOfOn,

    disconnect: account.disconnect,

    isBankPendingTxnsExcluded: account.isBankPendingTxnsExcluded,
    accountNumberMasked: account.accountNumberMasked,
  };

  const aggregator = account.aggregators && account.aggregators.size > 0 && account.aggregators.get(0);
  if (aggregator) {
    requestData.cpData = {
      aggregators: [
        {
          id: aggregator.id,
          channel: aggregator.channel,
          cpId: aggregator.cpId,
          cpLoginId: aggregator.cpLoginId,
          subType: aggregator.subType,
          accountName: aggregator.accountName,
          accountNumberMasked: aggregator.accountNumberMasked,
          normalizeOnlineBalance: aggregator.normalizeOnlineBalance,
          ...(aggregator.aggStatus ? { aggStatus: aggregator.aggStatus } : {}),
          ...(aggregator.lastRefreshAttemptedAt ? { lastRefreshAttemptedAt: aggregator.lastRefreshAttemptedAt } : {}),
          ...(aggregator.lastRefreshSuccessfulAt
            ? { lastRefreshSuccessfulAt: aggregator.lastRefreshSuccessfulAt }
            : {}),
        },
      ],
    };
  }
  return requestData;
}

export const transformAccountToRequestData = (account: accountsTypes.AccountProps): accountsTypes.AccountProps => {
  switch (account.type) {
    case 'ACCOUNTS_PAYABLE':
      return transformAccountsPayableToRequestData(account);
    case 'ACCOUNTS_RECEIVABLE':
      return transformAccountsReceivableToRequestData(account);
    case 'BANK':
      return transformBankAccountToRequestData(account);
    case 'CASH':
      return transformCashAccountToRequestData(account);
    case 'CREDIT':
      return transformCreditAccountToRequestData(account);
    case 'INVESTMENT':
      return transformInvestmentAccountToRequestData(account);
    case 'LOAN':
      return transformLoanAccountToRequestData(account);
    case 'OTHER_ASSET':
      return transformOtherAssetAccountToRequestData(account);
    case 'OTHER_LIABILITY':
      return transformOtherLiabilityAccountToRequestData(account);
    case 'PAYMENT':
      return transformPaymentAccountToRequestData(account);
    case 'REAL_ESTATE':
      return transformRealEstateAccountToRequestData(account);
    case 'VEHICLE':
      return transformVehicleAccountToRequestData(account);
    case 'GOAL':
      return transformUnknownAccountToRequestData(account);
    case 'BUSINESS':
      return transformBusinessAccountToRequestData(account);
    default:
      return transformUnknownAccountToRequestData(account);
  }
};

export function transformQcsAccountToAccount(qAccount: accountsTypes.AccountProps) {
  switch (qAccount.type) {
    case 'ACCOUNTS_PAYABLE':
      return transformApiDataToAccountsPayable(qAccount);
    case 'ACCOUNTS_RECEIVABLE':
      return transformApiDataToAccountsReceivable(qAccount);
    case 'CASH':
      return transformApiDataToCashAccount(qAccount);
    case 'BANK':
      return transformApiDataToBankAccount(qAccount);
    case 'CREDIT':
      return transformApiDataToCreditAccount(qAccount);
    case 'INVESTMENT':
      return transformApiDataToInvestmentAccount(qAccount);
    case 'LOAN':
      return transformApiDataToLoanAccount(qAccount);
    case 'OTHER_ASSET':
      return transformApiDataToOtherAssetAccount(qAccount);
    case 'OTHER_LIABILITY':
      return transformApiDataToOtherLiabilityAccount(qAccount);
    case 'PAYMENT':
      return transformApiDataToPaymentAccount(qAccount);
    case 'REAL_ESTATE':
      return transformApiDataToRealEstateAccount(qAccount);
    case 'VEHICLE':
      return transformApiDataToVehicleAccount(qAccount);
    case 'GOAL':
      return transformApiDataToGoalAccount(qAccount);
    case 'BUSINESS':
      return transformApiDataToBusinessAccount(qAccount);
    case 'UNKNOWN':
    default:
      return transformApiDataToUnknownAccount(qAccount);
  }
}

export const transformQcsAccountsToAccounts = (qcsAccounts: Array<accountsTypes.AccountProps>) =>
  qcsAccounts.map(transformQcsAccountToAccount);

export const transformResponseToAccounts = (
  response?: { data?: { resources?: Array<accountsTypes.AccountProps> } } | null,
) => transformQcsAccountsToAccounts(response?.data?.resources ?? []);

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Account API Responses to Accounts
// /////////////////////////////////////////////////////////////////////////////

function transformApiDataToAggregatorMap(data: accountsTypes.AccountAggregatorProps) {
  return {
    id: data.id,
    cpLoginId: data.cpLoginId,
    cpId: data.cpId,
    channel: data.channel,

    subType: data.subType,

    accountName: data.accountName,
    accountNumberMasked: data.accountNumberMasked,

    aggStatus: data.aggStatus,
    aggStatusCode: data.aggStatusCode,
    aggStatusDetail: data.aggStatusDetail,

    lastRefreshAttemptedAt: data.lastRefreshAttemptedAt,
    lastRefreshSuccessfulAt: data.lastRefreshSuccessfulAt,

    onlineBalance: data.onlineBalance,
    onlineBalanceAt: data.onlineBalanceAt,
    availableBalance: data.availableBalance,
    normalizeOnlineBalance: data.normalizeOnlineBalance,

    interestRate: data.interestRate,
    creditLimit: data.creditLimit,

    paymentMethods: data.paymentMethods ? ImList(data.paymentMethods) : undefined,
  };
}

function transformApiDataToAccountsPayable(data: accountsTypes.AccountsPayableProps) {
  return accountsTypes.mkAccountsPayable(transformApiDataToAccountMap(data));
}

function transformApiDataToAccountsReceivable(data: accountsTypes.AccountsReceivableProps) {
  return accountsTypes.mkAccountsReceivable({
    ...transformApiDataToAccountMap(data),
  });
}

function transformApiDataToBankAccount(data: accountsTypes.BankAccountProps & RemoteCpData) {
  return accountsTypes.mkBankAccount({
    ...transformApiDataToAccountMap(data),
    interestRate: data.interestRate,
    maturityDateOn: data.maturityDateOn,
    minimumNoFeeBalance: data.minimumNoFeeBalance,
    monthlyFee: data.monthlyFee,
    freeBillPay: data.freeBillPay,
    atmFeeReimbursement: data.atmFeeReimbursement,
    aggregators: !(data.cpData && data.cpData.aggregators)
      ? null
      : ImList(
          data.cpData.aggregators.map((aggregator) =>
            accountsTypes.mkAccountAggregator({
              ...transformApiDataToAggregatorMap(aggregator),
            }),
          ),
        ),
  });
}

function transformApiDataToCashAccount(data: accountsTypes.CashAccountProps) {
  return accountsTypes.mkCashAccount({
    ...transformApiDataToAccountMap(data),
  });
}

function transformApiDataToCreditAccount(data: accountsTypes.CreditAccountProps & RemoteCpData) {
  return accountsTypes.mkCreditAccount({
    ...(transformApiDataToAccountMap(data) as Partial<typeof data>),
    aggregators: !(data.cpData && data.cpData.aggregators)
      ? null
      : ImList(
          data.cpData.aggregators.map((aggregator) =>
            accountsTypes.mkAccountAggregator({
              ...transformApiDataToAggregatorMap(aggregator),
            }),
          ),
        ),
  });
}

function transformApiDataToGoalAccount(data: accountsTypes.GoalAccountProps) {
  return accountsTypes.mkGoalAccount({
    ...transformApiDataToAccountMap(data),
  });
}

function transformApiDataToInvestmentAccount(data: accountsTypes.InvestmentAccountProps & RemoteCpData) {
  return accountsTypes.mkInvestmentAccount({
    ...transformApiDataToAccountMap(data),
    aggregators: !(data.cpData && data.cpData.aggregators)
      ? null
      : ImList(
          data.cpData.aggregators.map((aggregator) =>
            accountsTypes.mkAccountAggregator({
              ...transformApiDataToAggregatorMap(aggregator),
            }),
          ),
        ),
  });
}

function transformApiDataToLoanAccount(data: accountsTypes.LoanAccountProps & RemoteCpData) {
  return accountsTypes.mkLoanAccount({
    ...(transformApiDataToAccountMap(data) as Partial<typeof data>),
    aggregators: !(data.cpData && data.cpData.aggregators)
      ? null
      : ImList(
          data.cpData.aggregators.map((aggregator) =>
            accountsTypes.mkAccountAggregator({
              ...transformApiDataToAggregatorMap(aggregator),
            }),
          ),
        ),
  });
}

function transformApiDataToOtherAssetAccount(data: accountsTypes.OtherAssetAccountProps & RemoteCpData) {
  return accountsTypes.mkOtherAssetAccount({
    ...transformApiDataToAccountMap(data),
    aggregators: !(data.cpData && data.cpData.aggregators)
      ? null
      : ImList(
          data.cpData.aggregators.map((aggregator) =>
            accountsTypes.mkAccountAggregator({
              ...transformApiDataToAggregatorMap(aggregator),
            }),
          ),
        ),
  });
}

function transformApiDataToOtherLiabilityAccount(data: accountsTypes.OtherLiabilityAccountProps & RemoteCpData) {
  return accountsTypes.mkOtherLiabilityAccount({
    ...transformApiDataToAccountMap(data),
    aggregators: !(data.cpData && data.cpData.aggregators)
      ? null
      : ImList(
          data.cpData.aggregators.map((aggregator) =>
            accountsTypes.mkAccountAggregator({
              ...transformApiDataToAggregatorMap(aggregator),
            }),
          ),
        ),
  });
}

function transformApiDataToPaymentAccount(data: accountsTypes.PaymentAccountProps & RemoteCpData) {
  return accountsTypes.mkPaymentAccount({
    ...transformApiDataToAccountMap(data),
    aggregators: !(data.cpData && data.cpData.aggregators)
      ? null
      : ImList(
          data.cpData.aggregators.map((aggregator) =>
            accountsTypes.mkAccountAggregator({
              ...transformApiDataToAggregatorMap(aggregator),
            }),
          ),
        ),
  });
}

function transformApiDataToRealEstateAccount(
  data: accountsTypes.RealEstateAccountProps & { address?: Address } & RemoteCpData,
) {
  return accountsTypes.mkRealEstateAccount({
    ...transformApiDataToAccountMap(data),
    line1: data.address ? data.address.line1 : null,
    line2: data.address ? data.address.line2 : null,
    city: data.address ? data.address.city : null,
    state: data.address ? data.address.state : null,
    zipCode: data.address ? data.address.zipCode : null,
    country: data.address ? data.address.country : null,
  });
}

function transformApiDataToVehicleAccount(data: accountsTypes.VehicleAccountProps & RemoteCpData) {
  return accountsTypes.mkVehicleAccount({
    ...transformApiDataToAccountMap(data),
    year: data.year,
    makeId: data.makeId,
    make: data.make,
    modelId: data.modelId,
    model: data.model,
    trimId: data.trimId,
    trim: data.trim,
    condition: data.condition,
    mileage: data.mileage,
    zipCode: data.zipCode,
    autoUpdateMiles: data.autoUpdateMiles,
    useCalculatedMilesPerMonth: data.useCalculatedMilesPerMonth,
    calculatedMilesPerMonth: data.calculatedMilesPerMonth,
    customMilesPerMonth: data.customMilesPerMonth,
    aggregators: !(data.cpData && data.cpData.aggregators)
      ? null
      : ImList(
          data.cpData.aggregators.map((aggregator) =>
            accountsTypes.mkAccountAggregator({
              ...transformApiDataToAggregatorMap(aggregator),
            }),
          ),
        ),
  });
}

function transformApiDataToUnknownAccount(data: accountsTypes.UnknownAccountProps & RemoteCpData) {
  return accountsTypes.mkUnknownAccount({
    ...transformApiDataToAccountMap(data),
    aggregators: !(data.cpData && data.cpData.aggregators)
      ? null
      : ImList(
          data.cpData.aggregators.map((aggregator) =>
            accountsTypes.mkAccountAggregator({
              ...transformApiDataToAggregatorMap(aggregator),
            }),
          ),
        ),
  });
}

function transformApiDataToBusinessAccount(data: accountsTypes.BusinessAccountProps & RemoteCpData) {
  return accountsTypes.mkBusinessAccount({
    ...transformApiDataToAccountMap(data),
    aggregators: !(data.cpData && data.cpData.aggregators)
      ? null
      : ImList(
          data.cpData.aggregators.map((aggregator) =>
            accountsTypes.mkAccountAggregator({
              ...transformApiDataToAggregatorMap(aggregator),
            }),
          ),
        ),
  });
}

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Accounts to Account API Request Data (batch)
// /////////////////////////////////////////////////////////////////////////////
export function transformAccountsToRequestData(accounts: ReadonlyArray<accountsTypes.AccountProps>) {
  return {
    resources: accounts.reduce<Array<accountsTypes.AccountProps>>((list, account) => {
      list.push(transformAccountToRequestData(account));
      return list;
    }, []),
  };
}
